/**
 * Switch — accessible boolean toggle built on Radix UI.
 *
 * Distinct from `ModeToggle` (which is the demo-specific
 * "vulnerable / CSK Protected" toggle with motion-spring physics):
 * `Switch` is the generic on/off control consumers reach for when a
 * boolean state needs an inline UI affordance. Keyboard activation
 * (`Space` / `Enter`) and ARIA `switch` role come from Radix.
 *
 * Usage:
 *
 *     <Switch checked={enabled} onCheckedChange={setEnabled} />
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "../../styling/index.js";

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>;

export const Switch = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(function Switch({ className, ...props }, ref) {
  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:bg-safe-500 data-[state=unchecked]:bg-neutral-400",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "pointer-events-none block size-4 rounded-full bg-white shadow-sm ring-0",
          "transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
        )}
      />
    </SwitchPrimitive.Root>
  );
});
